import { useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserActivities,
  selectIsActivitiesLoading,
  selectActivitiesPagination,
  selectActivityFilters,
  selectProjectDetails,
} from "../redux/selector";
import { actions } from "../redux/slice";
import { getActivityDateGroup, ActivityDateGroup } from "../helpers/Index";
import { IUserActivity } from "../type";

export const useLastActivities = () => {
  const dispatch = useDispatch();
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  const activities = useSelector(selectUserActivities);
  const isLoading = useSelector(selectIsActivitiesLoading);
  const pagination = useSelector(selectActivitiesPagination);
  const filters = useSelector(selectActivityFilters);

  const hasMore = pagination?.page < pagination?.totalPage;

  const handleProjectFilterChange = (projectIds: string[]) => {
    dispatch(actions.setActivityFilters({ ...filters, projectIds }));
    dispatch(actions.setActivitiesPagination({ ...pagination, page: 1 }));
    dispatch(actions.getUserActivities());
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      dispatch(
        actions.setActivitiesPagination({
          ...pagination,
          page: pagination.page + 1,
        }),
      );
      dispatch(actions.getUserActivities());
    }
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const groupedActivities = useMemo(() => {
    const groups: { group: string; items: IUserActivity[] }[] = [];
    const groupMap: Record<string, IUserActivity[]> = {};

    activities.forEach((activity) => {
      const groupName = getActivityDateGroup(activity.timestamp);
      if (!groupMap[groupName]) {
        groupMap[groupName] = [];
      }
      groupMap[groupName].push(activity);
    });
    const orderedGroups = Object.values(ActivityDateGroup);

    orderedGroups.forEach((groupKey) => {
      if (groupMap[groupKey]) {
        groups.push({ group: groupKey, items: groupMap[groupKey] });
      }
    });

    return groups;
  }, [activities]);

  const homeProjectDetails = useSelector(selectProjectDetails);

  const projectOptions = useMemo(() => {
    return homeProjectDetails.map((p) => ({
      id: p._id,
      value: p.name,
    }));
  }, [homeProjectDetails]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      handleLoadMore();
    }
  };

  return {
    groupedActivities,
    isLoading,
    selectedProjectIds: filters.projectIds || [],
    setSelectedProjectIds: handleProjectFilterChange,
    collapsedGroups,
    toggleGroup,
    hasMore,
    onLoadMore: handleLoadMore,
    homeProjectDetails,
    projectOptions,
    scrollRef,
    handleScroll,
  };
};
